// 本地存储服务 - 替代Supabase功能
class LocalStorageService {
  constructor() {
    this.prefix = 'catti_'
  }

  // 生成用户标识
  getUserIdentifier() {
    const adminToken = localStorage.getItem('adminToken')
    if (adminToken) {
      return `admin_${localStorage.getItem('adminUsername') || 'admin'}`
    }
    return 'anonymous'
  }

  // 保存笔记
  async saveNotes(noteType, content) {
    const userIdentifier = this.getUserIdentifier()
    const key = `${this.prefix}notes_${userIdentifier}_${noteType}`
    const data = {
      content,
      updated_at: new Date().toISOString(),
      user_identifier: userIdentifier,
      note_type: noteType
    }
    localStorage.setItem(key, JSON.stringify(data))
    console.log(`笔记已保存到本地: ${noteType}`)
    return { success: true }
  }

  // 加载笔记
  async loadNotes(noteType) {
    const userIdentifier = this.getUserIdentifier()
    const key = `${this.prefix}notes_${userIdentifier}_${noteType}`
    const data = localStorage.getItem(key)
    if (data) {
      const parsed = JSON.parse(data)
      console.log(`从本地加载笔记: ${noteType}`)
      return { data: parsed, error: null }
    }
    return { data: null, error: null }
  }

  // 保存高亮
  saveHighlights(filePath, highlights) {
    const userIdentifier = this.getUserIdentifier()
    const key = `${this.prefix}highlights_${userIdentifier}_${filePath}`
    localStorage.setItem(key, JSON.stringify(highlights))
    console.log(`高亮已保存到本地: ${filePath}`)
  }

  // 加载高亮
  loadHighlights(filePath) {
    const userIdentifier = this.getUserIdentifier()
    const key = `${this.prefix}highlights_${userIdentifier}_${filePath}`
    const data = localStorage.getItem(key)
    if (data) {
      console.log(`从本地加载高亮: ${filePath}`)
      return JSON.parse(data)
    }
    return []
  }

  // 保存翻译历史
  saveTranslationHistory(filePath, history) {
    const userIdentifier = this.getUserIdentifier()
    const key = `${this.prefix}translation_history_${userIdentifier}_${filePath}`
    localStorage.setItem(key, JSON.stringify(history))
    console.log(`翻译历史已保存到本地: ${filePath}`)
  }

  // 加载翻译历史
  loadTranslationHistory(filePath) {
    const userIdentifier = this.getUserIdentifier()
    const key = `${this.prefix}translation_history_${userIdentifier}_${filePath}`
    const data = localStorage.getItem(key)
    if (data) {
      console.log(`从本地加载翻译历史: ${filePath}`)
      return JSON.parse(data)
    }
    return []
  }

  // 保存用户设置
  saveUserSettings(settings) {
    const userIdentifier = this.getUserIdentifier()
    const key = `${this.prefix}settings_${userIdentifier}`
    localStorage.setItem(key, JSON.stringify(settings))
    console.log('用户设置已保存到本地')
  }

  // 加载用户设置
  loadUserSettings() {
    const userIdentifier = this.getUserIdentifier()
    const key = `${this.prefix}settings_${userIdentifier}`
    const data = localStorage.getItem(key)
    if (data) {
      console.log('从本地加载用户设置')
      return JSON.parse(data)
    }
    return {}
  }

  // 清理过期数据
  cleanupOldData() {
    const now = Date.now()
    const maxAge = 30 * 24 * 60 * 60 * 1000 // 30天
    const keys = Object.keys(localStorage)
    
    keys.forEach(key => {
      if (key.startsWith(this.prefix)) {
        try {
          const data = JSON.parse(localStorage.getItem(key))
          if (data.updated_at) {
            const age = now - new Date(data.updated_at).getTime()
            if (age > maxAge) {
              localStorage.removeItem(key)
              console.log(`清理过期数据: ${key}`)
            }
          }
        } catch (e) {
          // 忽略解析错误
        }
      }
    })
  }

  // 批改功能使用次数管理
  async getCorrectionUsage() {
    const userIdentifier = this.getUserIdentifier()
    const key = `${this.prefix}correction_usage_${userIdentifier}`
    const data = localStorage.getItem(key)
    if (data) {
      const parsed = JSON.parse(data)
      console.log(`从本地加载批改使用记录: ${userIdentifier}`)
      return parsed
    }
    return null
  }

  async saveCorrectionUsage(usage) {
    const userIdentifier = this.getUserIdentifier()
    const key = `${this.prefix}correction_usage_${userIdentifier}`
    const data = {
      ...usage,
      updated_at: new Date().toISOString(),
      user_identifier: userIdentifier
    }
    localStorage.setItem(key, JSON.stringify(data))
    console.log(`批改使用记录已保存到本地: ${userIdentifier}`)
    return { success: true }
  }

  async checkCorrectionLimit() {
    const today = new Date().toISOString().split('T')[0]
    const usage = await this.getCorrectionUsage()
    
    // 如果记录不存在或者日期不是今天，返回可以使用
    if (!usage || usage.date !== today) {
      return { canUse: true, count: 0, remainingCount: 2 }
    }
    
    // 非登录用户每天可以使用2次
    const maxCorrections = 2
    const canUse = usage.count < maxCorrections
    const remainingCount = Math.max(0, maxCorrections - usage.count)
    
    return { canUse, count: usage.count, remainingCount }
  }

  async incrementCorrectionCount() {
    const today = new Date().toISOString().split('T')[0]
    const usage = await this.getCorrectionUsage()
    
    if (!usage || usage.date !== today) {
      // 创建新记录
      const newUsage = {
        userId: this.getUserIdentifier(),
        date: today,
        count: 1,
        lastUsedTime: new Date().toISOString()
      }
      await this.saveCorrectionUsage(newUsage)
      return { count: 1, canUse: true, remainingCount: 1 }
    } else {
      // 增加使用次数
      usage.count += 1
      usage.lastUsedTime = new Date().toISOString()
      await this.saveCorrectionUsage(usage)
      
      const maxCorrections = 2
      const canUse = usage.count < maxCorrections
      const remainingCount = Math.max(0, maxCorrections - usage.count)
      
      return { count: usage.count, canUse, remainingCount }
    }
  }
}

// 创建全局实例
const localStorageService = new LocalStorageService()

// 导出兼容的接口
export const getSupabase = async () => {
  return localStorageService
}

export const supabase = localStorageService

// 导出其他必要的函数
export const initializeSupabase = async () => {
  console.log('本地存储服务已初始化')
  return localStorageService
}

export const resetSupabaseClient = async () => {
  console.log('本地存储服务重置')
  return localStorageService
}

// 导出事件常量
export const CONNECTION_EVENTS = {
  RESTORED: 'local-storage-restored',
  LOST: 'local-storage-lost',
  ERROR: 'local-storage-error'
}

export const ONLINE_STATUS = {
  ONLINE: 'online',
  OFFLINE: 'offline',
  AWAY: 'away'
}

// 导出其他必要的函数
export const updateUserOnlineStatus = async () => {
  // 本地存储不需要在线状态
  return true
}

export const cleanInactiveUsers = async () => {
  // 清理过期数据
  localStorageService.cleanupOldData()
  return true
}

export const dispatchConnectionEvent = (eventName, detail = {}) => {
  console.log(`本地存储事件: ${eventName}`, detail)
}

export default getSupabase